import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { api } from '../../api';
import { createSessionEventSource } from '../../services/realtimeService';
import { useT } from '../../localization/LocalizationProvider';

const MIN_PLAYER_COUNT = 1;
const MAX_PLAYER_COUNT = 15;
const PLAYER_COUNT_OPTIONS = Array.from({ length: MAX_PLAYER_COUNT - MIN_PLAYER_COUNT + 1 }, (_, index) => MIN_PLAYER_COUNT + index);

const emptyBootstrap = {
  adminDraftQuiz: null,
  questionSets: [],
  activeGameSession: null,
  wrongDelayOptions: [1, 2, 3, 5, 10]
};

const emptyLiveStatus = {
  gameStatus: 'idle',
  phase: 'idle',
  currentRoundIndex: 0,
  currentRoundTitle: null,
  activeQuizName: '',
  enabledPlayersCount: 0,
  joinedPlayersCount: 0,
  finishedPlayersCount: 0,
  waitingFor: [],
  players: []
};

const phaseLabels = {
  idle: 'idle',
  reset: 'reset',
  playing: 'playing',
  roundComplete: 'roundComplete',
  unlock: 'unlock',
  finished: 'finished',
  paused: 'paused',
  stopped: 'stopped'
};

const toneColor = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
  default: 'default'
};

function formatClock(timestamp) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '--:--';
  return date.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
}

function normalizeLogEntry(entry = {}) {
  return {
    id: entry.id ?? entry.Id ?? `log-${Math.random().toString(16).slice(2)}`,
    timestamp: entry.timestamp ?? entry.Timestamp ?? new Date().toISOString(),
    message: entry.message ?? entry.Message ?? entry.text ?? entry.Text ?? 'Događaj',
    type: entry.type ?? entry.Type ?? entry.tone ?? entry.Tone ?? 'event'
  };
}

function normalizePlayerCount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return MIN_PLAYER_COUNT;
  return Math.min(MAX_PLAYER_COUNT, Math.max(MIN_PLAYER_COUNT, Math.round(numeric)));
}

function buildEnabledPlayers(playerCount) {
  return Array.from({ length: normalizePlayerCount(playerCount) }, (_, index) => index + 1);
}

function normalizeQuizForForm(quiz) {
  if (!quiz) return quiz;
  const fallbackCount = Array.isArray(quiz.enabledPlayers) && quiz.enabledPlayers.length
    ? quiz.enabledPlayers.length
    : MIN_PLAYER_COUNT;
  const playerCount = normalizePlayerCount(quiz.playerCount ?? fallbackCount);
  return {
    ...quiz,
    playerCount,
    enabledPlayers: buildEnabledPlayers(playerCount)
  };
}

export function GameSettingsPage() {
  const t = useT();
  const [bootstrap, setBootstrap] = useState(emptyBootstrap);
  const [quiz, setQuiz] = useState(null);
  const [liveStatus, setLiveStatus] = useState(emptyLiveStatus);
  const [activityLog, setActivityLog] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [lastSaveAction, setLastSaveAction] = useState(null);
  const [lastRuntimeAction, setLastRuntimeAction] = useState(null);
  const [pendingAction, setPendingAction] = useState('');
  const [selectedSavedQuizId, setSelectedSavedQuizId] = useState('');

  const showFeedback = useCallback((severity, message, scope = 'runtime') => {
    const timestamp = new Date();
    setSnackbar({ open: true, severity, message });
    const nextAction = { severity, message, timestamp: formatClock(timestamp.toISOString()) };
    if (scope === 'save') {
      setLastSaveAction(nextAction);
      return;
    }
    setLastRuntimeAction(nextAction);
  }, []);

  const loadBootstrap = useCallback(async () => {
    const data = await api.getAdminBootstrap();
    setBootstrap(data);
    setQuiz(normalizeQuizForForm(data.adminDraftQuiz));
    setSelectedSavedQuizId((current) => (data.savedQuizzes.some((savedQuiz) => savedQuiz.id === current) ? current : ''));
  }, []);

  const loadLiveStatus = useCallback(async () => {
    const [statusResponse, logResponse] = await Promise.all([
      api.getAdminGameStatus(),
      api.getAdminGameLog()
    ]);
    setLiveStatus(statusResponse.data || emptyLiveStatus);
    setActivityLog((logResponse.data?.events || []).map((entry) => normalizeLogEntry(entry)));
  }, []);

  useEffect(() => {
    loadBootstrap();
    loadLiveStatus();
  }, [loadBootstrap, loadLiveStatus]);

  useEffect(() => {
    const source = createSessionEventSource();
    const refresh = () => {
      loadLiveStatus().catch(() => {});
    };

    if (source) {
      ['session:update', 'player:joined', 'player:progress', 'round:completed', 'round:unlocked', 'game:finished', 'game:stopped', 'game:paused', 'game:resumed', 'admin:update']
        .forEach((eventName) => source.addEventListener(eventName, refresh));
      source.onerror = () => {};
    }

    const interval = setInterval(() => {
      loadLiveStatus().catch(() => {});
    }, 5000);

    return () => {
      clearInterval(interval);
      if (source) {
        source.close();
      }
    };
  }, [loadLiveStatus]);

  const questionSetOptions = useMemo(() => bootstrap.questionSets || [], [bootstrap.questionSets]);

  const updateQuiz = (changes) => setQuiz((current) => normalizeQuizForForm({ ...current, ...changes }));
  const updateRound = (roundId, changes) => setQuiz((current) => normalizeQuizForForm({
    ...current,
    rounds: current.rounds.map((round) => (round.id === roundId ? { ...round, ...changes } : round))
  }));

  const runAction = async (actionKey, executor, options = {}) => {
    const { genericError = t('Messages.GenericActionFailed'), successSeverity = 'success', scope = 'runtime' } = options;
    setPendingAction(actionKey);
    try {
      const result = await executor();
      if (options.afterSuccess) {
        await options.afterSuccess(result);
      }
      showFeedback(successSeverity, result?.message || options.successMessage || t('Messages.GenericActionSuccess'), scope);
      return result;
    } catch (error) {
      showFeedback(error.code === 'INSUFFICIENT_QUESTIONS' ? 'warning' : 'error', error.message || genericError, scope);
      return null;
    } finally {
      setPendingAction('');
    }
  };

  if (!quiz) return null;

  const waitingForText = liveStatus.waitingFor?.length
    ? liveStatus.waitingFor.map((player) => player.name ? `${player.playerLabel} (${player.name})` : player.playerLabel).join(', ')
    : t('Messages.RoundAllFinished');

  const solvingPlayers = liveStatus.players.filter((player) => player.joined && !player.roundDone && player.tone === 'info');
  const finishedPlayers = liveStatus.players.filter((player) => player.roundDone);

  const isGameActivelyRunning = liveStatus.gameStatus === 'running';
  const statusLabels = {
    idle: t('Status.Idle'),
    reset: t('Status.Reset'),
    running: t('Status.Running'),
    stopped: t('Status.Stopped'),
    paused: t('Status.Paused'),
    completed: t('Status.Completed')
  };

  return (
    <Container>
      <Stack spacing={3}>
        <Typography variant="h4">{t('Admin.GameSettings.Title')}</Typography>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">{t('Admin.GameSettings.Basics')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('Admin.GameSettings.QuizName')}
                    helperText={t('Admin.GameSettings.QuizNameHelp')}
                    value={quiz.internalName}
                    onChange={(event) => updateQuiz({ internalName: event.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label={t('Admin.GameSettings.QuizPublicTitle')} value={quiz.publicTitle} onChange={(event) => updateQuiz({ publicTitle: event.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={3} label={t('Admin.GameSettings.IntroText')} value={quiz.introText} onChange={(event) => updateQuiz({ introText: event.target.value })} />
                </Grid>
              </Grid>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                <FormControlLabel control={<Checkbox checked={quiz.showLogo} onChange={(event) => updateQuiz({ showLogo: event.target.checked })} />} label={t('Admin.GameSettings.ShowLogo')} />
                <FormControlLabel control={<Checkbox checked={quiz.enableCorrectSound} onChange={(event) => updateQuiz({ enableCorrectSound: event.target.checked })} />} label={t('Admin.GameSettings.CorrectSound')} />
                <FormControlLabel control={<Checkbox checked={quiz.enableWrongSound} onChange={(event) => updateQuiz({ enableWrongSound: event.target.checked })} />} label={t('Admin.GameSettings.WrongSound')} />
                <FormControlLabel control={<Checkbox checked={quiz.enableWrongAnswerDelay} onChange={(event) => updateQuiz({ enableWrongAnswerDelay: event.target.checked })} />} label={t('Admin.GameSettings.WrongDelay')} />
              </Stack>
              <Box sx={{ maxWidth: 240 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>{t('Admin.GameSettings.WrongDelayDuration')}</Typography>
                <Select
                  fullWidth
                  value={quiz.wrongAnswerDelaySeconds}
                  disabled={!quiz.enableWrongAnswerDelay}
                  onChange={(event) => updateQuiz({ wrongAnswerDelaySeconds: Number(event.target.value) })}
                >
                  {bootstrap.wrongDelayOptions.map((option) => (
                    <MenuItem key={option} value={option}>{t('Admin.GameSettings.SecondsOption', { seconds: option })}</MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ maxWidth: 260 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('Admin.GameSettings.PlayerCount')}</Typography>
                <Select
                  fullWidth
                  value={quiz.playerCount}
                  onChange={(event) => {
                    const playerCount = Number(event.target.value);
                    updateQuiz({ playerCount, enabledPlayers: buildEnabledPlayers(playerCount) });
                  }}
                >
                  {PLAYER_COUNT_OPTIONS.map((playerCount) => (
                    <MenuItem key={playerCount} value={playerCount}>{playerCount}</MenuItem>
                  ))}
                </Select>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{t('Admin.GameSettings.Rounds')}</Typography>
                <Button
                  variant="outlined"
                  onClick={() => updateQuiz({
                    rounds: [...quiz.rounds, {
                      id: `round-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                      title: t('Admin.GameSettings.RoundNumber', { number: quiz.rounds.length + 1 }),
                      questionSetId: questionSetOptions[0]?.id || null,
                      questionsPerPlayer: 5,
                      secretWord: '',
                      completionMessage: t('Admin.GameSettings.RoundCompletedDefault')
                    }]
                  })}
                >
                  {t('Admin.GameSettings.AddRound')}
                </Button>
              </Stack>
              {quiz.rounds.map((round, index) => (
                <Card key={round.id} variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1">{t('Admin.GameSettings.RoundNumber', { number: index + 1 })}</Typography>
                        {quiz.rounds.length > 1 && (
                          <Button color="error" onClick={() => updateQuiz({ rounds: quiz.rounds.filter((item) => item.id !== round.id) })}>
                            {t('Admin.GameSettings.RemoveRound')}
                          </Button>
                        )}
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField fullWidth label={t('Admin.GameSettings.RoundTitle')} value={round.title} onChange={(event) => updateRound(round.id, { title: event.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id={`round-question-set-label-${round.id}`}>{t('Admin.GameSettings.QuestionSet')}</InputLabel>
                            <Select
                              labelId={`round-question-set-label-${round.id}`}
                              label={t('Admin.GameSettings.QuestionSet')}
                              value={round.questionSetId || ''}
                              onChange={(event) => updateRound(round.id, { questionSetId: event.target.value })}
                            >
                              {questionSetOptions.map((set) => (
                                <MenuItem key={set.id} value={set.id}>{set.name} ({set.questionCount})</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label={t('Admin.GameSettings.QuestionsPerPlayer')}
                            value={round.questionsPerPlayer}
                            onChange={(event) => updateRound(round.id, { questionsPerPlayer: Number(event.target.value) })}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth
                            label={index === quiz.rounds.length - 1 ? t('Admin.GameSettings.SecretWordLastRound') : t('Admin.GameSettings.SecretWord')}
                            value={round.secretWord}
                            onChange={(event) => updateRound(round.id, { secretWord: event.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label={t('Admin.GameSettings.RoundCompletionMessage')}
                            value={round.completionMessage}
                            onChange={(event) => updateRound(round.id, { completionMessage: event.target.value })}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">{t('Admin.GameSettings.SaveSection')}</Typography>
              <Typography color="text.secondary">
                {t('Admin.GameSettings.SaveSectionDescription')}
              </Typography>
              {quiz.id && (
                <Alert severity="info">
                  {t('Admin.GameSettings.EditingQuiz', { name: quiz.internalName })}
                </Alert>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Stack spacing={1} flex={1}>
                  <Button
                    variant="contained"
                    disabled={!!pendingAction || liveStatus.gameStatus === "running" || liveStatus.gameStatus === "stopped"}
                    onClick={() => runAction('saveDraft', () => api.updateDraftQuiz(quiz), {
                      successMessage: t('Admin.GameSettings.SaveDraftSuccess'),
                      genericError: t('Admin.GameSettings.SaveDraftError'),
                      scope: 'save',
                      afterSuccess: async (result) => {
                        setQuiz(normalizeQuizForForm(result.data));
                        await loadBootstrap();
                        await loadLiveStatus();
                      }
                    })}
                  >
                    {pendingAction === 'saveDraft' ? t('Common.Loading') : t('Buttons.SaveDraft')}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {t('Admin.GameSettings.SaveDraftHint')}
                  </Typography>
                </Stack>
                <Stack spacing={1} flex={1}>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={!!pendingAction}
                    onClick={() => {
                      if (!String(quiz.internalName || '').trim()) {
                        showFeedback('warning', t('Admin.GameSettings.SaveQuizNameRequired'), 'save');
                        return;
                      }
                      runAction('saveQuiz', () => (quiz.id ? api.updateQuiz(quiz.id, quiz) : api.saveQuiz(quiz)), {
                        successMessage: t('Admin.GameSettings.SaveQuizSuccess', { name: quiz.internalName }),
                        genericError: t('Admin.GameSettings.SaveQuizError'),
                        scope: 'save',
                        afterSuccess: async (result) => {
                          setQuiz(normalizeQuizForForm(result.data));
                          await loadBootstrap();
                          await loadLiveStatus();
                        }
                      });
                    }}
                  >
                    {pendingAction === 'saveQuiz' ? t('Common.Loading') : t('Buttons.SaveAsQuiz')}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {t('Admin.GameSettings.SaveQuizHint')}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Select
                  displayEmpty
                  fullWidth
                  value={selectedSavedQuizId}
                  onChange={(event) => setSelectedSavedQuizId(event.target.value)}
                >
                  <MenuItem value="">{t('Admin.GameSettings.SelectSavedQuiz')}</MenuItem>
                  {bootstrap.savedQuizzes.map((savedQuiz) => (
                    <MenuItem key={savedQuiz.id} value={savedQuiz.id}>{savedQuiz.internalName}</MenuItem>
                  ))}
                </Select>
                <Button
                  variant="outlined"
                  disabled={!!pendingAction || !selectedSavedQuizId}
                  onClick={() => runAction('loadQuiz', () => api.loadQuizToDraft(selectedSavedQuizId), {
                    scope: 'save',
                    genericError: t('Admin.GameSettings.LoadQuizError'),
                    afterSuccess: async (result) => {
                      setQuiz(normalizeQuizForForm(result.data));
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'loadQuiz' ? t('Common.Loading') : t('Buttons.LoadQuiz')}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('Admin.GameSettings.LoadQuizHint')}
              </Typography>
              {lastSaveAction && (
                <Alert severity={lastSaveAction.severity}>
                  {t('Admin.GameSettings.LastSaveAction', { message: lastSaveAction.message, time: lastSaveAction.timestamp })}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">{t('Admin.GameSettings.RuntimeControls')}</Typography>
              <Typography color="text.secondary">
                {t('Admin.GameSettings.RuntimeStatusLine', { status: statusLabels[liveStatus.gameStatus] || liveStatus.gameStatus, phase: phaseLabels[liveStatus.phase] || liveStatus.phase })}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrowRoundedIcon />}
                  disabled={!!pendingAction || liveStatus.gameStatus === "running" || liveStatus.gameStatus === "stopped"}
                  onClick={() => runAction('start', async () => {
                    await api.updateDraftQuiz(quiz);
                    return api.startGame();
                  }, {
                    successMessage: t('Admin.GameSettings.StartSuccess'),
                    genericError: t('Admin.GameSettings.StartError'),
                    scope: 'runtime',
                    afterSuccess: async () => {
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'start' ? t('Common.Loading') : liveStatus.gameStatus === 'paused' ? t('Buttons.ResumeGame') : t('Buttons.StartGame')}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PauseRoundedIcon />}
                  disabled={!!pendingAction || !isGameActivelyRunning}
                  onClick={() => runAction('pause', () => api.pauseGame(), {
                    successMessage: t('Admin.GameSettings.PauseSuccess'),
                    genericError: t('Admin.GameSettings.PauseError'),
                    scope: 'runtime',
                    afterSuccess: async () => {
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'pause' ? t('Common.Loading') : t('Buttons.PauseGame')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopRoundedIcon />}
                  disabled={!!pendingAction || !isGameActivelyRunning}
                  onClick={() => runAction('stop', () => api.stopGame(), {
                    successMessage: t('Admin.GameSettings.StopSuccess'),
                    genericError: t('Admin.GameSettings.StopError'),
                    scope: 'runtime',
                    afterSuccess: async () => {
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'stop' ? t('Common.Loading') : t('Buttons.StopGame')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ReplayRoundedIcon />}
                  disabled={!!pendingAction}
                  onClick={() => runAction('reset', () => api.resetGame(), {
                    successMessage: t('Admin.GameSettings.ResetSuccess'),
                    genericError: t('Admin.GameSettings.ResetError'),
                    scope: 'runtime',
                    afterSuccess: async () => {
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'reset' ? t('Common.Loading') : t('Buttons.Reset')}
                </Button>
              </Stack>
              {lastRuntimeAction && (
                <Alert severity={lastRuntimeAction.severity}>
                  {t('Admin.GameSettings.LastRuntimeAction', { message: lastRuntimeAction.message, time: lastRuntimeAction.timestamp })}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">{t('Admin.GameSettings.RuntimeStatus')}</Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                <Chip label={t('Messages.StatusGame', { value: statusLabels[liveStatus.gameStatus] || liveStatus.gameStatus })} color="primary" variant="outlined" />
                <Chip label={t('Messages.CurrentRound', { value: liveStatus.currentRoundTitle || (liveStatus.gameStatus === 'idle' ? '—' : t('Admin.GameSettings.RoundNumber', { number: liveStatus.currentRoundIndex + 1 })) })} variant="outlined" />
                <Chip label={t('Messages.Phase', { value: phaseLabels[liveStatus.phase] || liveStatus.phase })} variant="outlined" />
                <Chip label={t('Messages.ActiveQuiz', { value: liveStatus.activeQuizName || quiz.internalName || '—' })} variant="outlined" />
                <Chip label={t('Messages.EnabledPlayers', { count: liveStatus.enabledPlayersCount })} variant="outlined" />
                <Chip label={t('Messages.JoinedPlayers', { joined: liveStatus.joinedPlayersCount, enabled: liveStatus.enabledPlayersCount })} variant="outlined" />
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('Admin.GameSettings.CurrentStatus')}</Typography>
                <Typography color="text.secondary">
                  {t('Admin.GameSettings.CurrentlySolving', { value: solvingPlayers.length ? solvingPlayers.map((player) => player.name ? `${player.playerLabel} (${player.name})` : player.playerLabel).join(', ') : t('Messages.NoOne') })}
                </Typography>
                <Typography color="text.secondary">
                  {t('Admin.GameSettings.FinishedCurrentRound', { value: finishedPlayers.length ? finishedPlayers.map((player) => player.name ? `${player.playerLabel} (${player.name})` : player.playerLabel).join(', ') : t('Messages.NoOne') })}
                </Typography>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('Admin.GameSettings.PlayersStatus')}</Typography>
                <List dense>
                  {liveStatus.players.map((player) => (
                    <ListItem key={player.playerNo} divider>
                      <ListItemText
                        primary={`${player.playerLabel}${player.name ? ` — ${player.name}` : ''}`}
                        secondary={player.statusText}
                      />
                      <Chip size="small" color={toneColor[player.tone] || 'default'} label={player.roundDone ? t('Admin.GameSettings.PlayerFinished') : player.joined ? t('Admin.GameSettings.PlayerActive') : t('Admin.GameSettings.PlayerNotJoined')} />
                    </ListItem>
                  ))}
                </List>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('Admin.GameSettings.WaitingFor')}</Typography>
                <Alert severity={liveStatus.waitingFor?.length ? 'warning' : 'success'}>
                  {liveStatus.waitingFor?.length ? t('Admin.GameSettings.WaitingForValue', { value: waitingForText }) : waitingForText}
                </Alert>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('Admin.GameSettings.ActivityLog')}</Typography>
                <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <List dense>
                    {activityLog.map((event) => (
                      <ListItem key={event.id} divider>
                        <ListItemText
                          primary={`${formatClock(event.timestamp)} — ${event.message}`}
                          secondary={event.type}
                        />
                      </ListItem>
                    ))}
                    {!activityLog.length && (
                      <ListItem>
                        <ListItemText primary={t('Admin.GameSettings.NoEvents')} />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar((current) => ({ ...current, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
